import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { forkJoin, of, Subject } from "rxjs";
import { filter, first, switchMap, takeUntil } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { PhotoAndRemarkComponent } from "src/app/core/survey/shared/components/photo-and-remark/photo-and-remark.component";
import { PictureInfo } from "src/app/core/survey/shared/components/photo-and-remark/photo-viewmodel";
import { FormValidationService } from "src/app/core/survey/shared/services/formValidation.service";
import { SurveyType } from "src/app/core/survey/shared/services/photo.service";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";
import { SurveyPhotoTypeEnum } from "../../../../../../../shared/Models/enums/SurveyPhotoTypeEnum";
import {
  Building,
  InsideSurvey,
} from "../../../../../../../shared/Models/read";
import {
  InsideSurveyInsideInfo,
  INSIDE_SURVEY_INSIDE_TECHNICAL_INFO,
} from "../../../../config/inside-survey.magic-strings";
import { CopperIntroType } from "../../../../models/copperIntroType.enum";
import { ExistingTelcoCoax } from "../../../../models/existingTelcoCoax.enum";
import { ExistingTelcoConnections } from "../../../../models/existingTelcoConnections.enum";
import { ExistingVerticalCabling } from "../../../../models/existingVerticalCabling.enum";

@Component({
  selector: "app-existing-telco",
  templateUrl: "./existing-telco.component.html",
  styleUrls: ["./existing-telco.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExistingTelcoComponent),
      multi: true,
    },
  ],
})
export class ExistingTelcoComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  @Input() pictures: {
    insideCopperIntroPhotos: PictureInfo[];
    outsideCopperIntroPhotos: PictureInfo[];
    outsideFibreIntroPhotos: PictureInfo[];
    insideFibreIntroPhotos: PictureInfo[];
  };
  @Input() buildings: Building[];
  @Input() isAuthCanWrite: boolean;
  @Input() viewerLayer: HTMLDivElement;
  @Input() survey: InsideSurvey;
  @Input() cameraLayer: HTMLDivElement;
  @Input() camera: CameraComponent;

  isDisabled: boolean = false;
  existingTelcoForm: FormGroup = new FormGroup({});
  technicalInfoQuestionsBoolOptions = [
    { displayName: $localize`:@@general.yes:Yes`, value: true },
    { displayName: $localize`:@@general.no:No`, value: false },
  ];
  technicalInfoQuestionsCopperIntroTypeOptions = Object.keys(CopperIntroType)
    .filter((i) => isNaN(+i))
    .map((i) => {
      return { displayName: i, value: CopperIntroType[i] };
    });
  formChanges: Subject<Partial<InsideSurvey>> = new Subject<
    Partial<InsideSurvey>
  >();
  existingTelcoConnections = ExistingTelcoConnections;
  existingTelcoConnectionOptions = [
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.krone:Krone Blocks`,
      value: ExistingTelcoConnections.Krone_Blocks,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.siemens:Siemens blocks`,
      value: ExistingTelcoConnections.Siemens_Blocks,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.adc:ADC modules`,
      value: ExistingTelcoConnections.ADC_Modules,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.screwed:Screwed`,
      value: ExistingTelcoConnections.Screwed,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.soldered:Soldered`,
      value: ExistingTelcoConnections.Soldered,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.connections.answers.other:Other`,
      value: ExistingTelcoConnections.Other,
    },
  ];
  existingVerticalCabling = ExistingVerticalCabling;
  existingVerticalCablingOptions = [
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.fiber:Fiber`,
      value: ExistingVerticalCabling.Fiber,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.cat56:CAT5e or CAT6`,
      value: ExistingVerticalCabling.CAT5e_Or_CAT6,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.cat3vvt:CAT3 or VVT`,
      value: ExistingVerticalCabling.CAT3_Or_VVT,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.redwhitejumper:Red-White jumper`,
      value: ExistingVerticalCabling.Red_White_Jumper,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.flatcable:Flat cable`,
      value: ExistingVerticalCabling.Flat_Cable,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.verticalcabling.answer.other:Other`,
      value: ExistingVerticalCabling.Other,
    },
  ];
  existingTelcoCoax = ExistingTelcoCoax;
  existingTelcoCoaxOptions = [
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.coax.answer.multitaps:Multitaps (tech. room)`,
      value: ExistingTelcoCoax.Multiptaps,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.coax.answer.quantity:Quantity (tech. room)`,
      value: ExistingTelcoCoax.Quantity,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.coax.answer.multitapslanding:Multitaps landing`,
      value: ExistingTelcoCoax.Multitaps_Landing,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.coax.answer.directcoax:Direct coax`,
      value: ExistingTelcoCoax.Direct_Coax,
    },
    {
      label: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.coax.answer.other:Other`,
      value: ExistingTelcoCoax.Other,
    },
  ];
  nrOfDesiredFibreIntrosValue: number;
  nrOfCopperIntrosValue: number;
  surveyPhotoTypes = SurveyPhotoTypeEnum;
  selectedBuildingValue: string;
  refactoredCopperIntros = [];
  refactoredFibreIntros = [];

  @ViewChildren("fileInput")
  fileInputs: QueryList<ElementRef<HTMLInputElement>>;

  @ViewChildren(PhotoAndRemarkComponent)
  photoComponents: QueryList<PhotoAndRemarkComponent>;

  surveyType = SurveyType;
  surveyPhotoType = SurveyPhotoTypeEnum;

  insideCopperIntroPhotoRemarkFieldName = "insideCopperIntroPhotoRemark";
  outsideCopperIntroPhotoRemarkFieldName = "outsideCopperIntroPhotoRemark";
  insideFibreIntroPhotoRemarkFieldName = "insideFibreIntroPhotoRemark";
  outsideFibreIntroPhotoRemarkFieldName = "outsideFibreIntroPhotoRemark";

  insideSurveyInsideInfo = InsideSurveyInsideInfo;

  constructor(
    private fb: FormBuilder,
    private dialogService: DialogService,
    private formValidationService: FormValidationService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.buildForms();
    // this.selectedBuilding = first(this.buildings).id;
  }

  getErrors(subTitle?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[INSIDE_SURVEY_INSIDE_TECHNICAL_INFO, subTitle, field].filter(
        (x) => !!x
      )
    );
  }

  counter(i: number) {
    return new Array(i);
  }

  galleryClick(inputId: string, override: boolean) {
    let observable = of(true);
    if (override)
      observable = this.dialogService.openDialog({
        headerText: $localize`:@@photo.overwrite.header:Overwrite photo`,
        detailText: $localize`:@@photo.overwrite.detailtext:Uploading a new photo will overwrite the existing one. Are you sure?`,
        dialogType: DialogType.Continue,
      });

    observable.pipe(first()).subscribe((res) => {
      if (res)
        this.fileInputs
          .find((x) => x.nativeElement.id === inputId)
          .nativeElement.click();
    });
  }

  private buildForms() {
    this.existingTelcoForm = this.fb.group({
      individualCoaxCableFromBasement: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      copperIntroType: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      existingTelcoCoax: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      existingTelcoCoaxQuantity: [
        { value: "", disabled: !this.isAuthCanWrite },
        [Validators.min(0), Validators.max(99)],
      ],
      existingTelcoConnections: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      existingVerticalCabling: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      nrOfCopperIntros: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      nrOfDesiredFibreIntros: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
    });

    this.existingTelcoForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) =>
        this.formChanges.next({
          ...val,
        })
      );
  }

  deletePhotoForPhotoTypeAndIndex(
    index: number,
    photoType: SurveyPhotoTypeEnum
  ) {
    var outsidePhotoComponent = this.photoComponents.find(
      (e) => e.surveyPhotoType === photoType && e.fixedIndex === index
    );

    return outsidePhotoComponent.deletePhoto(
      outsidePhotoComponent.photoOnFocus
    );
  }

  fiberIntroCountChange(event) {
    this.nrOfDesiredFibreIntros = event;
  }

  copperIntroCountChange(event) {
    this.nrOfCopperIntros = event;
  }

  set nrOfDesiredFibreIntros(value: number) {
    if (value < 0) return;

    if (value < this.nrOfDesiredFibreIntros) {
      var indexToRemove = this.nrOfDesiredFibreIntros - 1;
      if (
        this.pictures.insideFibreIntroPhotos.find(
          (e) => e.index === indexToRemove
        ) ||
        this.pictures.outsideFibreIntroPhotos.find(
          (e) => e.index === indexToRemove
        )
      ) {
        this.dialogService
          .openDialog({
            headerText: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.deletefiber.header:Delete fiber intro`,
            detailText: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.deletefiber.detailtext:Deleting this fiber intro, will also remove the linked images, are you sure?`,
            dialogType: DialogType.Delete,
          })
          .pipe(
            first(),
            filter((feedback) => feedback),
            switchMap(() =>
              forkJoin([
                this.deletePhotoForPhotoTypeAndIndex(
                  indexToRemove,
                  SurveyPhotoTypeEnum.OutsideFibreIntro
                ),
                this.deletePhotoForPhotoTypeAndIndex(
                  indexToRemove,
                  SurveyPhotoTypeEnum.InsideFibreIntro
                ),
              ])
            )
          )
          .subscribe(() => {
            this.nrOfDesiredFibreIntrosValue = value;
            this.existingTelcoForm.controls.nrOfDesiredFibreIntros.setValue(
              value
            );
          });
      } else {
        this.nrOfDesiredFibreIntrosValue = value;

        this.existingTelcoForm.controls.nrOfDesiredFibreIntros.setValue(value);
      }
    } else {
      this.nrOfDesiredFibreIntrosValue = value;
      this.existingTelcoForm.controls.nrOfDesiredFibreIntros.setValue(value);
    }
  }

  set nrOfCopperIntros(value: number) {
    if (value < 0) return;

    if (value < this.nrOfCopperIntros) {
      var indexToRemove = this.nrOfCopperIntros - 1;
      if (
        this.pictures.insideCopperIntroPhotos.find(
          (e) => e.index === indexToRemove
        ) ||
        this.pictures.outsideCopperIntroPhotos.find(
          (e) => e.index === indexToRemove
        )
      ) {
        this.dialogService
          .openDialog({
            headerText: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.deletecopper.header:Delete copper intro`,
            detailText: $localize`:@@survey.inside.technicalinfo.inside.section.existingtelco.deletecopper.detailtext:Deleting this copper intro, will also remove the linked images, are you sure?`,
            dialogType: DialogType.Delete,
          })
          .pipe(
            first(),
            filter((feedback) => feedback),
            switchMap(() =>
              forkJoin([
                this.deletePhotoForPhotoTypeAndIndex(
                  indexToRemove,
                  SurveyPhotoTypeEnum.OutsideCopperIntro
                ),
                this.deletePhotoForPhotoTypeAndIndex(
                  indexToRemove,
                  SurveyPhotoTypeEnum.InsideCopperIntro
                ),
              ])
            )
          )
          .subscribe(() => {
            this.nrOfCopperIntrosValue = value;
            this.existingTelcoForm.controls.nrOfCopperIntros.setValue(value);
          });
      } else {
        this.nrOfCopperIntrosValue = value;

        this.existingTelcoForm.controls.nrOfCopperIntros.setValue(value);
      }
    } else {
      this.nrOfCopperIntrosValue = value;

      this.existingTelcoForm.controls.nrOfCopperIntros.setValue(value);
    }
  }

  get nrOfDesiredFibreIntros() {
    return this.nrOfDesiredFibreIntrosValue;
  }
  get nrOfCopperIntros() {
    return this.nrOfCopperIntrosValue;
  }

  writeValue(obj: Partial<InsideSurvey>): void {
    this.existingTelcoForm.patchValue({ ...obj });
    this.nrOfCopperIntrosValue = obj.nrOfCopperIntros || 0;
    this.nrOfDesiredFibreIntrosValue = obj.nrOfDesiredFibreIntros || 0;
  }

  registerOnChange(fn: any): void {
    this.formChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched();
  }

  onTouched = () => null;

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
